import { useEffect, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Box, FormControl, styled, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import BooleanRadioButtonGroup from '@/components/forms/BooleanRadioButtonGroup';
import type { ReservationFormInput } from '../schema/reservationForm.schema';
import type { WillCallContext } from '../machines/willCall.machine';
import { useReservationFormMachines } from '../ReservationFormMachinesContext';
import { useFieldActorSnapshot } from '../hooks/useFieldActorSnapshot';

const PREFIX = 'WillCallSelector';
const classes = {
  subctrl: `${PREFIX}-subctrl`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`& .${classes.subctrl}`]: {
    marginTop: theme.spacing(1),
  },
}));

export function WillCallSelector({ index }: { index: number }) {
  const { t } = useTranslation('common');
  const { control, setValue, getValues } =
    useFormContext<ReservationFormInput>();
  const { fieldRefs } = useReservationFormMachines();
  const actor = fieldRefs.willCall;
  useFieldActorSnapshot(actor);

  const fieldName = `trips.${index}.isWillCall` as `trips.${number}.isWillCall`;

  const watchedWillCall = useWatch({ control, name: fieldName }) ?? false;

  const previousLegWillCall =
    useWatch({
      control,
      name: `trips.${Math.max(index - 1, 0)}.isWillCall` as const,
    }) ?? false;
  const previousWillCall = index > 0 ? previousLegWillCall : false;

  useEffect(() => {
    const snapshot = actor.getSnapshot().context as WillCallContext;
    if (snapshot.values[index] !== watchedWillCall) {
      actor.send({ type: 'CHANGE', index, value: watchedWillCall });
    }
  }, [actor, index, watchedWillCall]);

  const prevWillCallRef = useRef<boolean | undefined>(undefined);
  useEffect(() => {
    const prev = prevWillCallRef.current;
    prevWillCallRef.current = watchedWillCall;
    if (prev === false && watchedWillCall) {
      setValue(`trips.${index}.pickupDateTime`, null, {
        shouldValidate: true,
        shouldDirty: true,
      });
      fieldRefs.pickupDateTime.send({ type: 'CHANGE', index, value: '' });

      setValue(`trips.${index}.pickupDate`, null, {
        shouldValidate: true,
        shouldDirty: true,
      });
      fieldRefs.pickupDate.send({
        type: 'CHANGE',
        index,
        value: '',
      });

      setValue(`trips.${index}.roundTripWillCall`, true, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [watchedWillCall, index, setValue, getValues, fieldRefs]);

  useEffect(() => {
    if (!previousWillCall) return;

    setValue(fieldName, true, {
      shouldValidate: true,
      shouldDirty: true,
    });
  }, [fieldName, previousWillCall, setValue]);

  return (
    <StyledBox>
      <FormControl component="fieldset" variant="standard" fullWidth>
        <Typography variant="h5" component="h3">
          {t('member.pickup')}
        </Typography>
        <BooleanRadioButtonGroup
          name={fieldName}
          control={control}
          trueText={t('member.optionCallTextWhenReady')}
          falseText={t('member.optionPickDateTime')}
          disabled={previousWillCall}
        />
      </FormControl>
    </StyledBox>
  );
}
